import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { RiPlayFill, RiStopFill, RiAttachmentLine, RiArrowUpSLine } from "react-icons/ri";
import { AGENT_ORDER, STACK_ICONS } from "../constants";
import { getSwarmWorkflowSteps } from "../utils/swarmWorkflow";
import { vizMarkdownComponents, noVizMarkdownComponents } from "../utils/markdownComponents";
import AskMode from "./subcomponents/AskMode";
import SwarmMode from "./subcomponents/SwarmMode";
import Response from "./subcomponents/Response";
import SwarmResponse from "./subcomponents/SwarmResponse";
import ConfirmationDialog from "./subcomponents/ConfirmationDialog";
import ModeSelector from "./subcomponents/ModeSelector";
import WorkflowChatView from "./subcomponents/WorkflowChatView";
import ScrollIndicatorStrip from "./subcomponents/ScrollIndicatorStrip";
import ModeScrollStrip from "./subcomponents/ModeScrollStrip";

function MainPanel({
  question, setQuestion, askQuestion, loading, response, selectedApis = [], availableAgents = [], chatHistory = [], cancelRequest, stopAsk, isActiveConvServerRunning = false, progressSteps = [], files = [], setFiles, pendingQuestion, pendingFiles = [], streamingPhase, answerMode, setAnswerMode, generateExpansiveAnswer, generatingExpansiveIndex, generateEvaluationReport, generatingEvaluationIndex, conversationId, API_URL, swarm = null, activeMode = "ask", setActiveMode = null, onSwarmStart = null,
  // Workflow mode
  workflowList = [], activeWorkflowId = null, onWorkflowSelect, workflowRunHistory = [], workflowActiveRuns = {}, onRunWorkflow, cancelWorkflowRun,
}) {
  const navigate = useNavigate();
  const [isMobileView, setIsMobileView] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia("(max-width: 1200px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mediaQuery = window.matchMedia("(max-width: 1200px)");
    const handleChange = (event) => {
      setIsMobileView(event.matches);
    };
    handleChange(mediaQuery);
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
    } else if (typeof mediaQuery.addListener === "function") {
      mediaQuery.addListener(handleChange);
    }
    return () => {
      if (typeof mediaQuery.removeEventListener === "function") {
        mediaQuery.removeEventListener("change", handleChange);
      } else if (typeof mediaQuery.removeListener === "function") {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  const showTooltipWithTimeout = (tooltipKey) => {
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
    }
    setActiveTooltip(tooltipKey);
    tooltipTimeoutRef.current = setTimeout(() => {
      setActiveTooltip(null);
      tooltipTimeoutRef.current = null;
    }, 3000);
  };

  const handleIconMouseEnter = (tooltipKey) => {
    const supportsHover = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches;
    if (supportsHover) {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
      setActiveTooltip(tooltipKey);
    }
  };

  const handleIconMouseLeave = () => {
    const supportsHover = typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(hover: hover)').matches;
    if (supportsHover) {
      if (!tooltipTimeoutRef.current) {
        setActiveTooltip(null);
      }
    }
  };

  const handleIconClick = (e, tooltipKey, mode = null, autoHide = true) => {
    if (e) e.stopPropagation();
    
    if (mode && setActiveMode) {
      setActiveMode(mode);
    }
    
    if (autoHide) {
      showTooltipWithTimeout(tooltipKey);
    } else {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
      setActiveTooltip(activeTooltip === tooltipKey ? null : tooltipKey);
    }
  };

  const [isAskSectionCollapsed, setIsAskSectionCollapsed] = useState(false);
  // Restore the input panel whenever the user switches modes so controls are visible.
  useEffect(() => { setIsAskSectionCollapsed(false); }, [activeMode]);
  const [activeAnswerView, setActiveAnswerView] = useState({});
  useEffect(() => { setActiveAnswerView({}); prevChatHistoryRef.current = []; }, [conversationId]);
  const [streamingActiveView, setStreamingActiveView] = useState('preliminary');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingExpansiveIndex, setPendingExpansiveIndex] = useState(null);
  const [showEvalConfirmDialog, setShowEvalConfirmDialog] = useState(false);
  const [pendingEvalIndex, setPendingEvalIndex] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [swarmMaxTurns, setSwarmMaxTurns] = useState(12);
  const dropdownRef = useRef(null);
  const [selectedSummaryIndex, setSelectedSummaryIndex] = useState(null);
  const [isReferencesExpanded, setIsReferencesExpanded] = useState(false);
  const [pauseActionPending, setPauseActionPending] = useState(false);
  const [resumeActionPending, setResumeActionPending] = useState(false);
  const [stopActionPending, setStopActionPending] = useState(false);
  const [usePriorContext, setUsePriorContext] = useState(false);

  useEffect(() => {
    if (swarm?.starting) {
      setSelectedSummaryIndex(null);
    }
  }, [swarm?.starting]);

  const isSwarmPausedForEffect = swarm?.swarmState === 'paused';
  const isSwarmTerminalForEffect = Boolean(swarm?.isTerminal);
  const isSwarmRunningForEffect = Boolean(swarm?.starting || swarm?.streaming);

  useEffect(() => {
    if (isSwarmPausedForEffect) setPauseActionPending(false);
    if (!isSwarmPausedForEffect) setResumeActionPending(false);
  }, [isSwarmPausedForEffect]);

  useEffect(() => {
    if (isSwarmTerminalForEffect) {
      setPauseActionPending(false);
      setResumeActionPending(false);
      setStopActionPending(false);
    }
  }, [isSwarmTerminalForEffect]);

  useEffect(() => {
    if (!isSwarmRunningForEffect && !isSwarmTerminalForEffect) {
      setPauseActionPending(false);
      setResumeActionPending(false);
      setStopActionPending(false);
    }
  }, [isSwarmRunningForEffect, isSwarmTerminalForEffect]);

  const displaySummary = useMemo(() => {
    if (selectedSummaryIndex !== null && swarm?.summaries?.[selectedSummaryIndex]) {
      return swarm.summaries[selectedSummaryIndex];
    }
    return swarm?.summary;
  }, [selectedSummaryIndex, swarm?.summaries, swarm?.summary]);

  useEffect(() => {
    setIsReferencesExpanded(false);
  }, [displaySummary]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAdvancedOptions(false);
      }
    };

    if (showAdvancedOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdvancedOptions]);

  useEffect(() => {
    if (streamingPhase === 'preliminary' && streamingActiveView !== 'preliminary') {
       setStreamingActiveView('preliminary');
    }
  }, [streamingPhase, streamingActiveView]);

  const [lastHistoryLength, setLastHistoryLength] = useState(0);
  const prevChatHistoryRef = useRef([]);
  useEffect(() => {
    if (chatHistory.length > lastHistoryLength) {
      const newEntries = chatHistory.slice(lastHistoryLength);

      setActiveAnswerView(prev => {
        const next = { ...prev };
        let hasChanges = false;

        newEntries.forEach(entry => {
          if (entry.preliminary_answer && entry.index !== undefined && !next[entry.index]) {
            next[entry.index] = streamingActiveView;
            hasChanges = true;
          }
        });

        return hasChanges ? next : prev;
      });
      setLastHistoryLength(chatHistory.length);
    } else if (chatHistory.length < lastHistoryLength) {
      setLastHistoryLength(chatHistory.length);
    }
  }, [chatHistory, lastHistoryLength, streamingActiveView]);

  // Keep prevChatHistoryRef in sync so other effects that compare old vs new
  // history entries remain correct.
  useEffect(() => {
    prevChatHistoryRef.current = chatHistory;
  }, [chatHistory]);

  // Stable identity so the memoized per-entry rows (Response.HistoryEntry)
  // don't re-render every streamed token; only stable setters are called.
  const handleExpansiveClick = useCallback((messageIndex) => {
    setPendingExpansiveIndex(messageIndex);
    setShowConfirmDialog(true);
  }, []);

  const handleConfirmExpansive = () => {
    if (pendingExpansiveIndex !== null && generateExpansiveAnswer) {
      generateExpansiveAnswer(pendingExpansiveIndex);
    }
    setShowConfirmDialog(false);
    setPendingExpansiveIndex(null);
  };

  const handleCancelExpansive = () => {
    setShowConfirmDialog(false);
    setPendingExpansiveIndex(null);
  };

  const handleEvalClick = useCallback((messageIndex) => {
    setPendingEvalIndex(messageIndex);
    setShowEvalConfirmDialog(true);
  }, []);

  const handleConfirmEval = () => {
    if (pendingEvalIndex !== null && generateEvaluationReport) {
      generateEvaluationReport(pendingEvalIndex, null);
    }
    setShowEvalConfirmDialog(false);
    setPendingEvalIndex(null);
  };

  const handleCancelEval = () => {
    setShowEvalConfirmDialog(false);
    setPendingEvalIndex(null);
  };

  const currentResponseRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const responseContainerRef = useRef(null);
  const scrolledForConvRef = useRef(null);
  const prevLoadingRef = useRef(false);
  const prevConvIdRef = useRef(null);
  const userScrolledRef = useRef(false);
  const cardRef = useRef(null);
  // Start at 0 — ResizeObserver updates it after first paint.
  // Avoids ~130px of excess blank space when the card is smaller than 180px.
  const [cardHeight, setCardHeight] = useState(0);

  const fileObjectUrls = useMemo(() => {
    const urls = new Map();
    files.forEach((file, index) => {
      urls.set(index, URL.createObjectURL(file));
    });
    return urls;
  }, [files]);

  const pendingFileObjectUrls = useMemo(() => {
    const urls = new Map();
    pendingFiles.forEach((file, index) => {
      urls.set(index, URL.createObjectURL(file));
    });
    return urls;
  }, [pendingFiles]);

  useEffect(() => {
    return () => {
      fileObjectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [fileObjectUrls]);

  useEffect(() => {
    return () => {
      pendingFileObjectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [pendingFileObjectUrls]);

  // force=true overrides the user-scrolled guard (used for intentional navigations).
  // force=false (default) respects the user's scroll position during streaming.
  const scrollToBottom = useCallback((force = false) => {
    if (responseContainerRef.current && (force || !userScrolledRef.current)) {
      responseContainerRef.current.scrollTop = responseContainerRef.current.scrollHeight;
    }
  }, []);

  // Mark when the user manually scrolls up so the streaming auto-scroll backs off.
  // Clear the flag when they scroll back to within 50px of the bottom.
  useEffect(() => {
    const container = responseContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      userScrolledRef.current = distanceFromBottom > 50;
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to bottom when a new query is submitted — always, resetting any prior scroll.
  useEffect(() => {
    if (pendingQuestion) {
      userScrolledRef.current = false;
      scrollToBottom(true);
    }
  }, [pendingQuestion, scrollToBottom]);

  // A stream's last line can be its error, so the follow below may stop first.
  useEffect(() => {
    if (response?.error) scrollToBottom();
  }, [response?.error, scrollToBottom]);

  // Follow streaming output; backs off if the user has scrolled up.
  useEffect(() => {
    if (loading) {
      scrollToBottom();
      scrollIntervalRef.current = setInterval(scrollToBottom, 400);
    }
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
    };
  }, [loading, scrollToBottom]);

  // Scroll to bottom when swarm generates a new summary during an active session.
  // Guarded on activeMode so stale swarm props in other modes never hijack scroll.
  const prevSummaryCountRef = useRef(0);
  const swarmSummaryRef = useRef(null);
  useEffect(() => {
    if (activeMode !== 'swarm') return;
    const count = swarm?.summaries?.length ?? 0;
    if (count > prevSummaryCountRef.current) {
      prevSummaryCountRef.current = count;
      userScrolledRef.current = false;
      const raf = requestAnimationFrame(() => scrollToBottom(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [activeMode, swarm?.summaries?.length, scrollToBottom]);

  // Swarm session activation: scroll to the summary so users start there and
  // can scroll up to read the debate. Falls back to bottom when no summary exists.
  // Pre-seeds prevSummaryCountRef so the "new summary" effect above doesn't
  // double-fire when the past session's summaries first load.
  useEffect(() => {
    if (
      activeMode !== 'swarm' ||
      !conversationId ||
      conversationId === scrolledForConvRef.current
    ) return;
    const eventsReady = (swarm?.events?.length ?? 0) > 0;
    const summaryReady = Boolean(displaySummary);
    if (!eventsReady && !summaryReady) return;

    scrolledForConvRef.current = conversationId;
    prevSummaryCountRef.current = swarm?.summaries?.length ?? 0;
    userScrolledRef.current = false;

    const raf = requestAnimationFrame(() => {
      if (summaryReady && swarmSummaryRef.current && responseContainerRef.current) {
        const container = responseContainerRef.current;
        const summaryRect = swarmSummaryRef.current.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        container.scrollTop = container.scrollTop + (summaryRect.top - containerRect.top) - 16;
      } else {
        scrollToBottom(true);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [activeMode, conversationId, swarm?.events?.length, swarm?.summaries?.length, displaySummary, scrollToBottom]);

  // Ask mode conversation activation: scroll to the beginning of the last
  // response so users start reading from the top of it.
  // Waits for chatHistory to be populated (handles async fetch).
  useEffect(() => {
    // A query that just finished in the SAME conversation must NOT
    // reposition the user: they were following the stream at the bottom
    // (e.g. still reading the concise answer) and an auto scroll-to-top of
    // the card would yank them away. Any conversation change is a genuine
    // navigation and always scrolls to the top of the last answer — the
    // same-conversation guard keeps that intact even when switching away
    // mid-query. We still mark the conversation as positioned so a later
    // navigation to it scrolls right.
    const justFinishedQuery =
      prevLoadingRef.current &&
      !loading &&
      prevConvIdRef.current === conversationId;
    prevLoadingRef.current = loading;
    prevConvIdRef.current = conversationId;
    if (
      activeMode === "ask" &&
      conversationId &&
      conversationId !== scrolledForConvRef.current &&
      chatHistory.length > 0 &&
      !loading
    ) {
      scrolledForConvRef.current = conversationId;
      if (justFinishedQuery) return;
      userScrolledRef.current = false;
      const raf = requestAnimationFrame(() => {
        const container = responseContainerRef.current;
        if (!container) return;

        const sorted = [...chatHistory].sort((a, b) => {
          const ai = typeof a.index === 'number' ? a.index : 0;
          const bi = typeof b.index === 'number' ? b.index : 0;
          return ai - bi;
        });
        const lastEntry = sorted[sorted.length - 1];
        const lastKey = lastEntry?.index ?? 0;
        const el = container.querySelector(`[data-entry-index="${lastKey}"]`);

        if (el) {
          const containerRect = container.getBoundingClientRect();
          const elRect = el.getBoundingClientRect();
          container.scrollTop = container.scrollTop + (elRect.top - containerRect.top) - 16;
        } else {
          scrollToBottom(true);
        }
      });
      return () => cancelAnimationFrame(raf);
    }
  // chatHistory.length (not chatHistory) is intentional — the effect is gated on
  // conversationId change and only needs to know whether data has arrived, not rerun
  // on every array mutation during streaming.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, chatHistory.length, loading, scrollToBottom]);

  // Measure the input card height so the response area's paddingBottom stays
  // equal to the card height — keeping content fully readable above the card.
  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setCardHeight(entry.contentRect.height + 16); // +16 for card margin
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setActiveTooltip(null);
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const textareaStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: isMobileView ? "0.5rem" : "0.75rem",
    borderRadius: "6px",
    fontSize: isMobileView ? "1rem" : "1.1rem",
    marginBottom: isMobileView ? 6 : 12,
    resize: "vertical",
    minHeight: isMobileView ? "2rem" : "2.5rem",
    lineHeight: "1.4",
  };

  const handleKeyDown = (e) => {
    if (isMobileView && e.key === 'Enter') {
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      
      if (activeMode === "swarm") {
        if (!swarm?.isActive || swarm?.isTerminal) {
          if (question.trim() || files.length > 0) {
            handleStartSwarm();
          }
        } else {
          if (question.trim() || files.length > 0) {
            handleInterveneSwarm();
          }
        }
      } else if (activeMode === "ask") {
        if (!loading && (question.trim() || files.length > 0)) {
          askQuestion(e);
        }
      } else if (activeMode === "workflow") {
        const wfRunning = activeWorkflowId &&
          (workflowActiveRuns[activeWorkflowId]?.status === 'pending' ||
           workflowActiveRuns[activeWorkflowId]?.status === 'running');
        if (!wfRunning && question.trim()) {
          onRunWorkflow && onRunWorkflow(question.trim());
          setQuestion('');
        }
      }
    }
  };
  
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };
  
  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getIconsToShow = () => {
    if (selectedApis === 'orchestrator') {
      return AGENT_ORDER.filter(agent => 
        (agent === 'orchestrator' || availableAgents.includes(agent)) && STACK_ICONS[agent]
      );
    } else {
      return [selectedApis];
    }
  };

  const iconsToShow = getIconsToShow();

  const buildPriorContext = (events) => {
    if (!events || events.length === 0) return null;
    const entries = events
      .filter(e =>
        e.type === 'human_initial_question' ||
        e.type === 'agent_message' ||
        e.type === 'human_intervention_received'
      )
      .map(e => {
        if (e.type === 'human_initial_question') {
          return {
            speaker_id: 'human',
            content: e.payload?.content || '',
            turn_index: e.payload?.turn_index || 0,
          };
        }
        if (e.type === 'agent_message') {
          return {
            speaker_id: e.payload?.speaker_id || 'agent',
            content: e.payload?.content || '',
            turn_index: e.payload?.turn_index || 0,
          };
        }
        // human_intervention_received
        return {
          speaker_id: 'human',
          content: e.payload?.content || '',
          turn_index: e.payload?.applied_at_turn || 0,
        };
      })
      .filter(e => e.content);
    return entries.length > 0 ? entries : null;
  };

  const handleStartSwarm = async (e) => {
    if (e) e.preventDefault();
    if (!swarm?.startSwarm) return;

    const q = question;
    const f = files;
    const mt = swarmMaxTurns;
    const priorContext = usePriorContext ? buildPriorContext(swarm?.events) : null;
    onSwarmStart?.(q);
    setQuestion("");
    setFiles([]);
    setUsePriorContext(false);

    await swarm.startSwarm({ question: q, files: f, maxTurns: mt, priorContext });
  };

  const handleInterveneSwarm = async (e) => {
    if (e) e.preventDefault();
    if (!swarm?.intervene || (!question.trim() && files.length === 0)) return;
    await swarm.intervene({ content: question, files: files });
    setQuestion("");
    setFiles([]);
  };

  const handlePauseSwarm = async () => {
    if (!swarm?.pauseSwarm) return;
    setPauseActionPending(true);
    await swarm.pauseSwarm({ reason: "Paused by user" });
  };

  const handleResumeSwarm = async () => {
    if (!swarm?.resumeSwarm) return;
    setResumeActionPending(true);
    await swarm.resumeSwarm({ reason: "Resumed by user" });
  };

  const handleStopSwarm = async (force = false) => {
    if (!swarm?.stopSwarm) return;
    setStopActionPending(true);
    await swarm.stopSwarm({ force, reason: "Stopped by user" });
  };

  const isSwarmRunning = swarm?.starting || swarm?.streaming;
  const isSwarmTerminal = swarm?.isTerminal;
  const isSwarmStopping = swarm?.stopping;
  const isSwarmPaused = swarm?.swarmState === 'paused';
  const isPauseUpdating = swarm?.pauseUpdating;
  const currentPauseMode = swarm?.status?.pause_mode || 'none';
  const isDelayUpdating = swarm?.delayUpdating;
  const currentTurnDelay = swarm?.status?.turn_delay ?? 0;
  const swarmWorkflowSteps = getSwarmWorkflowSteps(swarm?.events, isSwarmRunning);

  const hasSummarizableContent = useMemo(() => {
    if (!swarm?.events) return false;
    return swarm.events.some(evt => 
      evt.type === "agent_message" || 
      evt.type === "user_intervention" || 
      evt.type === "human_intervention_received"
    );
  }, [swarm?.events]);

  const swarmIsTerminal = Boolean(swarm?.isTerminal);
  const swarmIsSummarizing = Boolean(swarm?.summarizing);
  // Once we have any summary in state, ignore the server's pending flag —
  // it may lag behind the stream event that already delivered the summary.
  const hasSummaryResult = (swarm?.summaries?.length ?? 0) > 0;
  const summaryPending = !hasSummaryResult && Boolean(swarm?.status?.completion?.summary_pending);
  const summaryInProgress = swarmIsSummarizing || summaryPending;

  const isSummarizeDisabled = !swarm?.isActive || summaryInProgress || swarm?.starting || swarm?.stopping || !hasSummarizableContent;

  const terminalSummaryFallback = useMemo(() => {
    if (
      !swarmIsTerminal ||
      Boolean(displaySummary) ||
      !hasSummarizableContent
    ) {
      return null;
    }

    const summaryErrorMessage = swarm?.status?.completion?.summary_error?.message;
    if (summaryErrorMessage) {
      return "Automatic summary was unavailable for this session. Click \"Summarize\" to retry.";
    }

    if (summaryInProgress) {
      return "Session finished. Summary is being generated...";
    }

    return "Session finished. Summary is not available yet. Click \"Summarize\" to generate one.";
  }, [
    displaySummary,
    hasSummarizableContent,
    summaryInProgress,
    swarm?.status?.completion?.summary_error?.message,
    swarmIsTerminal,
  ]);


  return (
    <div style={{ position: "relative", flex: "1 1 auto", display: "flex", flexDirection: "column", height: "100%" }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        multiple
        accept="image/png,image/jpeg,image/jpg,image/webp,.pdf,.txt"
      />
      
      {/* Response area — always takes full remaining height.
          paddingBottom matches the card height so no content is hidden behind it. */}
      <div style={{ flex: "1 1 auto", display: "flex", minHeight: 0, position: "relative" }}>
        <div
          ref={responseContainerRef}
          data-testid="response-scroll"
          style={{
            flex: "1 1 auto", overflowY: "auto", minHeight: 0,
            paddingBottom: cardHeight + 8, position: "relative", paddingTop: 0,
          }}
        >
          {/* Width-constrained reading column — aligns with input card below */}
          <div style={{ maxWidth: '780px', margin: '0 auto', width: '100%', boxSizing: 'border-box', padding: isMobileView ? '0 12px' : '0' }}>
            {activeMode === "ask" ? (
              <Response
                response={response}
                loading={loading}
                isActiveConvServerRunning={isActiveConvServerRunning}
                currentResponseRef={currentResponseRef}
                streamingActiveView={streamingActiveView}
                setStreamingActiveView={setStreamingActiveView}
                streamingPhase={streamingPhase}
                pendingQuestion={pendingQuestion}
                pendingFiles={pendingFiles}
                pendingFileObjectUrls={pendingFileObjectUrls}
                chatHistory={chatHistory}
                activeAnswerView={activeAnswerView}
                setActiveAnswerView={setActiveAnswerView}
                generatingExpansiveIndex={generatingExpansiveIndex}
                generateExpansiveAnswer={generateExpansiveAnswer}
                generatingEvaluationIndex={generatingEvaluationIndex}
                generateEvaluationReport={generateEvaluationReport}
                conversationId={conversationId}
                API_URL={API_URL}
                markdownComponents={vizMarkdownComponents}
                handleExpansiveClick={handleExpansiveClick}
                handleEvalClick={handleEvalClick}
                progressSteps={progressSteps}
              />
            ) : activeMode === "workflow" ? (
              <WorkflowChatView
                workflowRunHistory={workflowRunHistory}
                workflowActiveRuns={workflowActiveRuns}
                activeWorkflowId={activeWorkflowId}
              />
            ) : (
              <SwarmResponse
                terminalSummaryFallback={terminalSummaryFallback}
                displaySummary={displaySummary}
                swarm={swarm}
                selectedSummaryIndex={selectedSummaryIndex}
                setSelectedSummaryIndex={setSelectedSummaryIndex}
                isReferencesExpanded={isReferencesExpanded}
                setIsReferencesExpanded={setIsReferencesExpanded}
                markdownComponents={vizMarkdownComponents}
                markdownComponentsNoViz={noVizMarkdownComponents}
                summaryRef={swarmSummaryRef}
                swarmWorkflowSteps={swarmWorkflowSteps}
                isSwarmRunning={isSwarmRunning}
                isSwarmTerminal={isSwarmTerminal}
              />
            )}
          </div>
        </div>

        {activeMode === "ask" && chatHistory.length > 0 && (
          <ScrollIndicatorStrip
            containerRef={responseContainerRef}
            chatHistory={chatHistory}
            onExpansiveClick={handleExpansiveClick}
            onEvalClick={handleEvalClick}
            isRunning={loading || isActiveConvServerRunning}
          />
        )}
        {activeMode === "swarm" && (swarm?.events?.length ?? 0) > 0 && (
          <ModeScrollStrip containerRef={responseContainerRef} />
        )}
        {activeMode === "workflow" && workflowRunHistory.length > 0 && (
          <ModeScrollStrip containerRef={responseContainerRef} />
        )}
      </div>

      {/* Input panel — bottom-anchored when content exists; centred vertically
          when the view is empty so first-use feels welcoming rather than bare. */}
      {(() => {
        const isEmpty =
          (activeMode === "ask"      && chatHistory.length === 0 && !loading && !pendingQuestion) ||
          (activeMode === "swarm"    && (swarm?.events?.length ?? 0) === 0) ||
          (activeMode === "workflow" && workflowRunHistory.length === 0);

        const HEADLINES = {
          ask:      { title: "Ask the stack",     sub: "Get comprehensive, multi-perspective answers from your AI agent stack." },
          swarm:    { title: "Debate the stack",  sub: "Let multiple AI agents debate your topic from different analytical angles." },
          workflow: { title: "Run a workflow",    sub: "Execute multi-step AI pipelines — search, extract, analyse, and synthesise." },
        };
        const hl = HEADLINES[activeMode] ?? HEADLINES.ask;

        return (
        <div ref={cardRef} style={{
          position: "absolute",
          ...(isEmpty ? {
            top: "50%", transform: "translateY(-50%)",
            bottom: "auto",
          } : {
            bottom: 0, top: "auto", transform: "none",
          }),
          left: 0,
          // Offset by scroll-strip width only when strips are visible.
          right: !isEmpty && activeMode === "ask"      && chatHistory.length > 0         ? '10px'
               : !isEmpty && activeMode === "swarm"    && (swarm?.events?.length ?? 0) > 0 ? '8px'
               : !isEmpty && activeMode === "workflow" && workflowRunHistory.length > 0  ? '8px'
               : 0,
          maxWidth: '840px', margin: '0 auto', boxSizing: 'border-box', zIndex: 2,
        }}>
          {/* ── Empty-state headline — only when there is nothing in the view ── */}
          {isEmpty && (
            <div style={{
              textAlign: "center",
              paddingBottom: "1.75rem",
              paddingLeft: isMobileView ? "1rem" : "0.75rem",
              paddingRight: isMobileView ? "1rem" : "0.75rem",
            }}>
              <h1 style={{
                fontSize: isMobileView ? "1.6rem" : "2.25rem",
                fontWeight: 700,
                margin: "0 0 0.5rem",
                color: "var(--text-color)",
                lineHeight: 1.2,
              }}>
                {hl.title}
              </h1>
              <p style={{
                fontSize: isMobileView ? "0.9rem" : "1rem",
                color: "var(--text-secondary, #888)",
                margin: 0,
                lineHeight: 1.5,
              }}>
                {hl.sub}
              </p>
              {activeMode === "workflow" && (
                <p style={{
                  fontSize: isMobileView ? "0.78rem" : "0.85rem",
                  color: "var(--text-secondary, #888)",
                  margin: "0.75rem auto 0",
                  maxWidth: "480px",
                  lineHeight: 1.5,
                  padding: "0.5rem 0.75rem",
                  background: "var(--hover-color, rgba(99,108,255,0.06))",
                  border: "1px solid var(--border-color, #e0e0e0)",
                  borderRadius: "6px",
                }}>
                  Workflows use your <strong>saved</strong> agent model 
                  configuration — not the current sidebar selection. To change 
                  which models are used, update your agent models in 
                  the{" "} <strong>Model Defaults</strong> tab of your profile 
                  and click <strong>Save</strong>. You can also override models 
                  per-agent directly in the <strong>workflow builder</strong> via 
                  each agent node's Subagent Overrides — those take 
                  precedence over your saved defaults.
                </p>
              )}
            </div>
          )}
        {isAskSectionCollapsed ? (
          /* ── COMPACT INLINE BAR (all devices) ────────────────────────
             Mirrors Claude.ai / Gemini: a pill-shaped input with an
             attach button on the left, a text field in the middle, an
             expand (↑) chevron, and a play/stop button on the right.   */
          (() => {
            const isRunning =
              (activeMode === "ask" && (loading || isActiveConvServerRunning)) ||
              (activeMode === "workflow" && !!(activeWorkflowId && (
                workflowActiveRuns[activeWorkflowId]?.status === "pending" ||
                workflowActiveRuns[activeWorkflowId]?.status === "running"
              )));
            const compactPlaceholder =
              activeMode === "swarm"
                ? (isSwarmRunning && !isSwarmTerminal ? "Inject guidance..." : "Type your topic for the swarm debate...")
                : activeMode === "workflow"
                ? (activeWorkflowId ? "Type your input here..." : "Select a workflow first...")
                : "Write your question here...";
            const handleCompactAction = async () => {
              if (activeMode === "ask") {
                if (loading || isActiveConvServerRunning) { cancelRequest(true); stopAsk(); }
                else if (question.trim() || files.length > 0) { askQuestion({ preventDefault: () => {} }); }
              } else if (activeMode === "swarm") {
                if (!isSwarmRunning || isSwarmTerminal) { if (question.trim() || files.length > 0) await handleStartSwarm(); }
                else { if (question.trim() || files.length > 0) await handleInterveneSwarm(); }
              } else if (activeMode === "workflow") {
                const wfRunning = activeWorkflowId && (
                  workflowActiveRuns[activeWorkflowId]?.status === "pending" ||
                  workflowActiveRuns[activeWorkflowId]?.status === "running");
                if (wfRunning) {
                  const runId = workflowActiveRuns[activeWorkflowId]?.runId;
                  if (activeWorkflowId && runId) cancelWorkflowRun(activeWorkflowId, runId);
                } else if (question.trim()) { onRunWorkflow && onRunWorkflow(question.trim()); setQuestion(""); }
              }
            };
            const playDisabled = activeMode === "workflow" && !activeWorkflowId && !isRunning;
            return (
              <div style={{ margin: isMobileView ? "0" : "0 0.75rem" }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  background: "var(--panel-background, var(--input-background))",
                  borderRadius: isMobileView ? "12px" : "16px",
                  border: "1px solid var(--border-color)",
                  boxShadow: "0 -4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
                  padding: "8px 10px",
                  minHeight: "52px",
                }}>
                  {/* Attach button — shown for Ask and Swarm modes */}
                  {activeMode !== "workflow" && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current.click()}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "var(--text-secondary, #888)", padding: "4px",
                        display: "flex", alignItems: "center", flexShrink: 0,
                        borderRadius: "6px", transition: "color 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--primary-color, #636cff)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-secondary, #888)"; }}
                      title="Attach file"
                    >
                      <RiAttachmentLine size={20} />
                    </button>
                  )}

                  {/* Inline textarea */}
                  <textarea
                    placeholder={compactPlaceholder}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={handleKeyDown}
                    rows={1}
                    style={{
                      flex: 1, background: "none", border: "none", outline: "none",
                      resize: "none", color: "var(--text-color)", fontSize: "1rem",
                      lineHeight: "1.5", padding: 0, fontFamily: "inherit",
                      overflowY: "hidden",
                    }}
                  />

                  {/* Play / Stop button */}
                  <button
                    type="button"
                    onClick={playDisabled ? undefined : handleCompactAction}
                    disabled={playDisabled}
                    style={{
                      background: isRunning
                        ? "var(--color-danger, #dc3545)"
                        : playDisabled
                          ? "var(--text-secondary, #aaa)"
                          : "var(--primary-color, #636cff)",
                      border: "none", borderRadius: "10px",
                      width: "36px", height: "36px", flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: playDisabled ? "not-allowed" : "pointer",
                      color: "#fff", opacity: playDisabled ? 0.5 : 1,
                      transition: "background 0.15s",
                    }}
                    title={
                      isRunning ? "Stop"
                        : playDisabled ? "Select a workflow first"
                        : activeMode === "swarm" && isSwarmRunning && !isSwarmTerminal ? "Send guidance"
                        : activeMode === "workflow" ? "Run"
                        : activeMode === "swarm" ? "Start"
                        : "Ask"
                    }
                  >
                    {isRunning ? <RiStopFill size={16} /> : <RiPlayFill size={16} />}
                  </button>

                  {/* Expand to full card — always last/rightmost */}
                  <button
                    type="button"
                    onClick={() => setIsAskSectionCollapsed(false)}
                    style={{
                      background: "none", border: "none", cursor: "pointer",
                      color: "var(--text-secondary, #888)", padding: "4px",
                      display: "flex", alignItems: "center", flexShrink: 0,
                      borderRadius: "6px", transition: "color 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = "var(--primary-color, #636cff)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = "var(--text-secondary, #888)"; }}
                    title="Expand input panel"
                  >
                    <RiArrowUpSLine size={20} />
                  </button>
                </div>
              </div>
            );
          })()
        ) : (
          /* ── FULL FLOATING CARD ───────────────────────────────────── */
          <div style={{
            margin: isMobileView ? "0" : "0 0.75rem",
            background: "var(--panel-background, var(--input-background))",
            borderRadius: isMobileView ? "12px" : "16px",
            border: "1px solid var(--border-color)",
            boxShadow: "0 -4px 20px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
            padding: "0 8px 8px",
            position: "relative",
          }}>
            <ModeSelector
              activeMode={activeMode}
              setActiveMode={setActiveMode}
              iconsToShow={iconsToShow}
              handleIconMouseEnter={handleIconMouseEnter}
              handleIconMouseLeave={handleIconMouseLeave}
              handleIconClick={handleIconClick}
              activeTooltip={activeTooltip}
              workflowList={workflowList}
              activeWorkflowId={activeWorkflowId}
              onWorkflowSelect={onWorkflowSelect}
              isMobile={isMobileView}
            />

            {activeMode === "ask" || activeMode === "workflow" ? (
              <AskMode
                question={question}
                setQuestion={setQuestion}
                handleKeyDown={handleKeyDown}
                textareaStyle={textareaStyle}
                askModeHint={
                  activeMode === "workflow" && activeWorkflowId
                    ? (
                      <button type="button" onClick={() => navigate(`/workflow-builder?load=${encodeURIComponent(activeWorkflowId)}`)}
                        style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-color, #636cff)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                        Open workflow in builder ↗
                      </button>
                    )
                    : activeMode === "workflow"
                      ? (
                        <button type="button" onClick={() => navigate('/workflow-builder')}
                          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary-color, #636cff)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}>
                          Open workflow builder ↗
                        </button>
                      )
                      : null
                }
                files={files}
                fileObjectUrls={fileObjectUrls}
                removeFile={removeFile}
                loading={activeMode === "workflow"
                  ? !!(activeWorkflowId && (
                      workflowActiveRuns[activeWorkflowId]?.status === 'pending' ||
                      workflowActiveRuns[activeWorkflowId]?.status === 'running'))
                  : loading}
                cancelRequest={activeMode === "workflow" ? () => {} : cancelRequest}
                stopAsk={activeMode === "workflow"
                  ? () => {
                      const runId = workflowActiveRuns[activeWorkflowId]?.runId;
                      if (activeWorkflowId && runId) cancelWorkflowRun(activeWorkflowId, runId);
                    }
                  : stopAsk}
                isActiveConvServerRunning={activeMode === "workflow" ? false : isActiveConvServerRunning}
                isMobileView={isMobileView}
                placeholder={activeMode === "workflow"
                  ? (activeWorkflowId ? "Type your input here..." : "Select a workflow to start a run.")
                  : "Write your question here..."}
                submitDisabled={activeMode === "workflow" && !activeWorkflowId}
                hideFileUpload={activeMode === "workflow"}
                fileInputRef={fileInputRef}
                askQuestion={activeMode === "workflow"
                  ? (e) => {
                      e && e.preventDefault && e.preventDefault();
                      const wfRunning = activeWorkflowId && (
                        workflowActiveRuns[activeWorkflowId]?.status === 'pending' ||
                        workflowActiveRuns[activeWorkflowId]?.status === 'running');
                      if (!wfRunning && question.trim()) {
                        onRunWorkflow && onRunWorkflow(question.trim());
                        setQuestion('');
                      }
                    }
                  : askQuestion}
                activeMode={activeMode}
                onHide={() => setIsAskSectionCollapsed(true)}
              />
            ) : (
              <SwarmMode
                question={question}
                setQuestion={setQuestion}
                handleKeyDown={handleKeyDown}
                textareaStyle={textareaStyle}
                files={files}
                fileObjectUrls={fileObjectUrls}
                removeFile={removeFile}
                isSwarmRunning={isSwarmRunning}
                isSwarmTerminal={isSwarmTerminal}
                handleStartSwarm={handleStartSwarm}
                handleInterveneSwarm={handleInterveneSwarm}
                isSwarmStopping={isSwarmStopping || stopActionPending}
                swarm={swarm}
                isMobileView={isMobileView}
                isPauseUpdating={isPauseUpdating || pauseActionPending || resumeActionPending}
                isSwarmPaused={isSwarmPaused}
                handleResumeSwarm={handleResumeSwarm}
                handlePauseSwarm={handlePauseSwarm}
                isSummarizeDisabled={isSummarizeDisabled}
                summaryInProgress={summaryInProgress}
                swarmMaxTurns={swarmMaxTurns}
                setSwarmMaxTurns={setSwarmMaxTurns}
                fileInputRef={fileInputRef}
                currentPauseMode={currentPauseMode}
                isDelayUpdating={isDelayUpdating}
                currentTurnDelay={currentTurnDelay}
                usePriorContext={usePriorContext}
                setUsePriorContext={setUsePriorContext}
                hasPriorContextAvailable={isSwarmTerminal && (swarm?.events?.length ?? 0) > 0}
                onHide={() => setIsAskSectionCollapsed(true)}
                onStop={() => handleStopSwarm(false)}
              />
            )}

          </div>
        )}
        </div>
        );
      })()}

      <ConfirmationDialog
        show={showConfirmDialog}
        onCancel={handleCancelExpansive}
        onConfirm={handleConfirmExpansive}
        title="Generate Expansive Answer"
      >
        An expansive answer doesn't currently exist, do you want to
        generate a detailed and comprehensive answer using specialized
        agents. This process may take longer than the initial response.
      </ConfirmationDialog>

      <ConfirmationDialog
        show={showEvalConfirmDialog}
        onCancel={handleCancelEval}
        onConfirm={handleConfirmEval}
        title="Generate Evaluation Report"
      >
        Do you want to generate a detailed evaluation report for this answer?
        This will use the Evaluator Agent to assess accuracy, completeness, and quality.
      </ConfirmationDialog>
    </div>
  );
}

MainPanel.propTypes = {
  // Conversation / ask
  question:                  PropTypes.string,
  setQuestion:               PropTypes.func,
  askQuestion:               PropTypes.func,
  loading:                   PropTypes.bool,
  response:                  PropTypes.object,
  selectedApis:              PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  availableAgents:           PropTypes.array,
  chatHistory:               PropTypes.array,
  cancelRequest:             PropTypes.func,
  stopAsk:                   PropTypes.func,
  isActiveConvServerRunning: PropTypes.bool,
  progressSteps:             PropTypes.array,
  files:                     PropTypes.array,
  setFiles:                  PropTypes.func,
  pendingQuestion:           PropTypes.string,
  pendingFiles:              PropTypes.array,
  streamingPhase:            PropTypes.string,
  answerMode:                PropTypes.object,
  setAnswerMode:             PropTypes.func,
  generateExpansiveAnswer:   PropTypes.func,
  generatingExpansiveIndex:  PropTypes.number,
  generateEvaluationReport:  PropTypes.func,
  generatingEvaluationIndex: PropTypes.number,
  conversationId:            PropTypes.string,
  API_URL:                   PropTypes.string,
  // Swarm
  swarm:                     PropTypes.object,
  onSwarmStart:              PropTypes.func,
  // Mode
  activeMode:                PropTypes.string,
  setActiveMode:             PropTypes.func,
  // Workflow
  workflowList:              PropTypes.array,
  activeWorkflowId:          PropTypes.string,
  onWorkflowSelect:          PropTypes.func,
  workflowRunHistory:        PropTypes.array,
  workflowActiveRuns:        PropTypes.object,
  onRunWorkflow:             PropTypes.func,
  cancelWorkflowRun:         PropTypes.func,
};

export default MainPanel;
